// =============================================
// WorkspaceSwitchDialog — modal dla zmiany aktywnego workspace
// =============================================
// Otwierany z WorkspaceSwitcher.tsx (button "Zmień workspace"). Pokazuje
// 2 klikalne karty (Firma / Sponsoraki) z preview wyboru, pomarańczowa
// info o billingu, footer Anuluj / Przełącz na X.
//
// Local state `localSelected` umożliwia preview wyboru przed commit
// (klikanie kart przełącza border, dopiero "Przełącz" wywoła onSelect).
// Reset preview na każde otwarcie modal (useEffect na `open`).
// =============================================

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowLeftRight, Check } from "lucide-react";
import type { Workspace, WorkspaceMeta } from "@/hooks/useWorkspace";
import {
  WORKSPACE_COLORS,
  WORKSPACE_ICONS,
  WORKSPACE_SHORT_NAMES,
} from "./workspaceTheme";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentWorkspace: Workspace;
  workspaces: WorkspaceMeta[];
  onSelect: (next: Workspace) => void;
}

export function WorkspaceSwitchDialog({
  open,
  onOpenChange,
  currentWorkspace,
  workspaces,
  onSelect,
}: Props) {
  const [localSelected, setLocalSelected] = useState<Workspace>(currentWorkspace);

  // Reset preview na każde otwarcie modal — żeby user nie zostawił poprzedniego
  // "kliknięcia" gdy zamknął i otworzył ponownie
  useEffect(() => {
    if (open) setLocalSelected(currentWorkspace);
  }, [open, currentWorkspace]);

  const selectedColors = WORKSPACE_COLORS[localSelected];
  const selectedShortName = WORKSPACE_SHORT_NAMES[localSelected];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Przełącz workspace</DialogTitle>
          <DialogDescription>
            Wybierz zestaw kluczy API i konto rozliczeniowe.
          </DialogDescription>
        </DialogHeader>

        {/* 2 klikalne karty per workspace */}
        <div className="space-y-2">
          {workspaces.map((ws) => {
            const isActive = ws.id === currentWorkspace;
            const isPreviewSelected = localSelected === ws.id;
            const Icon = WORKSPACE_ICONS[ws.id];
            const colors = WORKSPACE_COLORS[ws.id];
            const shortName = WORKSPACE_SHORT_NAMES[ws.id];

            return (
              <button
                key={ws.id}
                type="button"
                onClick={() => setLocalSelected(ws.id)}
                className={cn(
                  "w-full flex items-start gap-3 p-3 rounded-md transition text-left",
                  isPreviewSelected
                    ? "border-2"
                    : "border border-zinc-800 hover:border-zinc-700",
                )}
                style={
                  isPreviewSelected
                    ? { borderColor: colors.accent, backgroundColor: colors.bg100 }
                    : undefined
                }
              >
                <Icon
                  className="h-[18px] w-[18px] mt-0.5 shrink-0"
                  style={{ color: colors.accent }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="text-sm font-semibold"
                      style={{ color: colors.accent }}
                    >
                      {shortName}
                    </span>
                    {isActive && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0"
                        style={{ color: "#5DCAA5", backgroundColor: "#0a2418" }}
                      >
                        aktywny
                      </span>
                    )}
                    {isPreviewSelected && !isActive && (
                      <Check
                        className="h-4 w-4 shrink-0"
                        style={{ color: "#5DCAA5" }}
                      />
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                    {ws.description}
                  </p>
                  {/* Spojnosc: obie nazwy z label.split — "Klucze: Sponsoraki · billing → konto Sponsoraki"
                      zamiast mieszanki "SIDEPROJECTS · billing → konto Sponsoraki" */}
                  <p
                    className="text-[10px] mt-1"
                    style={{ color: colors.accent, opacity: 0.7 }}
                  >
                    Klucze: {shortName} · billing → konto {shortName}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Pomaranczowa info — dluzszy tekst niz w sidebar pastylce */}
        <div
          className="rounded-md px-3 py-2 flex items-start gap-2"
          style={{
            backgroundColor: "#2a1f0a",
            borderColor: "#EF9F27",
            borderWidth: "0.5px",
            borderStyle: "solid",
          }}
        >
          <AlertTriangle
            className="h-3.5 w-3.5 shrink-0 mt-0.5"
            style={{ color: "#EF9F27" }}
          />
          <span
            className="text-[11px] leading-snug"
            style={{ color: "#FAC775" }}
          >
            Następne operacje (LLM, FAL, SERP) pójdą na wybrane konto. Dane w
            bazie nie ruszane.
          </span>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button
            disabled={localSelected === currentWorkspace}
            onClick={() => onSelect(localSelected)}
            style={{
              backgroundColor: selectedColors.accent,
              color: selectedColors.dark,
            }}
          >
            <ArrowLeftRight className="h-3.5 w-3.5 mr-1.5" />
            Przełącz na {selectedShortName}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
